import React, { useState, useEffect } from "react";
import styles from "./ReferralsPage.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { getReferralHistory } from "../../../redux/actions/referral/getReferralHistory";
import { getReferralLinks } from "../../../redux/actions/referral/getReferralLinks";
import { createReferralLink } from "../../../redux/actions/referral/createReferralLink";
import { currencyFormat } from "../../../utils/currencyFormat";
import {apiUpdateReferralName} from "./utils"

const ReferralsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    userProfile: { data: userProfileData },
  } = useSelector((state) => state.user);

  const { referralHistory, referralLinks, createLink } = useSelector(
    (state) => state.referral
  );

  const [localLinks, setLocalLinks] = useState([]);
  const [saveStatus, setSaveStatus] = useState({});
  const [copiedCodeId, setCopiedCodeId] = useState(null);

  useEffect(() => {
    if (!userProfileData?.partner) {
      navigate("/");
    }
  }, [userProfileData, navigate]);

  useEffect(() => {
    dispatch(getReferralHistory());
    dispatch(getReferralLinks());
  }, [dispatch]);

  useEffect(() => {
    console.log("Full Referral History State:", referralHistory);
    console.log("Referral History Data:", referralHistory.data);
    console.log("Referral History Data Type:", typeof referralHistory.data);
    console.log("Is Array?", Array.isArray(referralHistory.data));
    if (referralHistory.data) {
      console.log("Data Keys:", Object.keys(referralHistory.data));
    }
  }, [referralHistory]);

  useEffect(() => {
    if (referralLinks.data) {
      setLocalLinks(referralLinks.data);
    }
  }, [referralLinks.data]);

  const handleCreateLink = () => {
    dispatch(createReferralLink());
  };

  if (!userProfileData?.partner) {
    return null;
  }

  const handleNameChange = (linkId, newName) => {
    setLocalLinks(prevLinks =>
      prevLinks.map(link =>
        link.id === linkId ? { ...link, readable_name: newName } : link
      )
    );
    
    const currentStatus = saveStatus[linkId];
    if (currentStatus === 'saved' || currentStatus === 'error') {
      setSaveStatus(prev => ({ ...prev, [linkId]: 'idle' }));
    }
  };
  
  const handleSaveClick = async (linkId) => {
    const linkToSave = localLinks.find(link => link.id === linkId);
    if (!linkToSave) return;

    const newName = linkToSave.readable_name;

    try {
      setSaveStatus(prev => ({ ...prev, [linkId]: 'saving' }));
      
      await apiUpdateReferralName(linkId, newName);

      setSaveStatus(prev => ({ ...prev, [linkId]: 'saved' }));
      
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [linkId]: 'idle' }));
      }, 2000);

    } catch (error) {
      console.error('Failed to update name:', error);
      setSaveStatus(prev => ({ ...prev, [linkId]: 'error' }));
    }
  };

  const renderStatusIndicator = (linkId) => {
    const status = saveStatus[linkId];
    
    if (status === 'saving') {
      return <span style={{ marginLeft: '8px', color: 'gray' }}>...</span>;
    }
    if (status === 'saved') {
      return <span style={{ marginLeft: '8px', color: 'green' }}>✅</span>;
    }
    if (status === 'error') {
      return <span style={{ marginLeft: '8px', color: 'red' }}>❌</span>;
    }
    return null;
  };

  const extractReferralCode = (completeLink) => {
    try {
      const url = new URL(completeLink);
      return url.searchParams.get('ref') || '';
    } catch (error) {
      // Fallback: try to extract manually if URL parsing fails
      const match = completeLink.match(/[?&]ref=([^&]+)/);
      return match ? match[1] : '';
    }
  };

  const handleCopyReferralCode = async (referralCode, linkId) => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopiedCodeId(linkId);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedCodeId(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy referral code:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralCode;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCodeId(linkId);
        setTimeout(() => {
          setCopiedCodeId(null);
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const renderReferralLinks = () => {
    // if (!Array.isArray(localLinks) || localLinks.length === 0) {
    //   return <p>Нет доступных реферальных ссылок</p>;
    // }

    return (
      <>
        <div className={styles.actionsContainer}>
          <button
            className={styles.createButton}
            onClick={handleCreateLink}
            disabled={createLink.loading}
          >
            {createLink.loading ? "Создание..." : "Создать ссылку"}
          </button>
          {createLink.error && (
            <p className={styles.error}>Ошибка: {createLink.error}</p>
          )}
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Код</th>
                <th>Дата создания</th>
                <th>Статус</th>
                <th>Полная ссылка</th>
                <th>Реферальный код</th>
                <th>Источник</th>
                <th>Количество</th>
              </tr>
            </thead>
            <tbody>
              {localLinks.map((link) => {
                const status = saveStatus[link.id];
                const isSaving = status === 'saving';
                const referralCode = extractReferralCode(link.complete_link);
                
                return (
                  <tr key={link.id}>
                    <td>{link.id}</td>
                    <td>{link.code}</td>
                    <td>{new Date(link.created_at).toLocaleDateString()}</td>
                    <td>{link.is_active ? "Активна" : "Неактивна"}</td>
                    <td>
                      <a
                        href={link.complete_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.complete_link}
                      </a>
                    </td>
                    <td>
                      <span
                        onClick={() => handleCopyReferralCode(referralCode, link.id)}
                        className={`tw-cursor-pointer tw-px-3 tw-py-1.5 tw-rounded-md tw-transition-all tw-duration-200 tw-select-none tw-inline-flex tw-items-center tw-gap-1.5 tw-font-mono tw-text-sm ${
                          copiedCodeId === link.id
                            ? 'tw-bg-green-500 tw-text-white tw-font-bold tw-border-none'
                            : 'tw-bg-gray-100 tw-text-gray-800 tw-font-medium tw-border tw-border-gray-300 hover:tw-bg-gray-200 hover:tw-border-blue-500'
                        }`}
                        title="Нажмите, чтобы скопировать"
                      >
                        {copiedCodeId === link.id ? (
                          <>
                            <span>✓</span>
                            <span>Скопировано!</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                            <span>{referralCode}</span>
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={link.readable_name || ''}
                          onChange={(e) => handleNameChange(link.id, e.target.value)}
                          placeholder="Название источника..."
                          style={{ width: '150px' }}
                          disabled={isSaving}
                        />
                        <button
                          onClick={() => handleSaveClick(link.id)}
                          disabled={isSaving}
                          style={{ marginLeft: '8px' }}
                        >
                          {isSaving ? '...' : 'Сохранить'}
                        </button>
                        
                        {renderStatusIndicator(link.id)}
                      </div>
                    </td>
                    <td>{link.count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderReferralHistory = () => {
    if (
      !referralHistory.data ||
      !Array.isArray(referralHistory.data.referrals)
    ) {
      return <p>Нет данных о рефералах</p>;
    }

    const {
      referrals,
      total_earnings,
      pending_earnings,
      referral_earnings,
      promo_earnings,
      promo_bonuses,
    } = referralHistory.data;

    const hasPromo = Array.isArray(promo_bonuses) && promo_bonuses.length > 0;

    return (
      <>
        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <h4>Общий заработок</h4>
            <p>{currencyFormat(total_earnings)}</p>
            {hasPromo && (
              <small style={{ color: "#888", fontSize: "0.8em" }}>
                Рефералы: {currencyFormat(referral_earnings || 0)} + Промо: {currencyFormat(promo_earnings || 0)}
              </small>
            )}
          </div>
          <div className={styles.statCard}>
            <h4>Ожидаемый заработок</h4>
            <p>{currencyFormat(pending_earnings)}</p>
          </div>
        </div>

        <h4 style={{ marginTop: "16px", marginBottom: "8px" }}>История рефералов</h4>
        {referrals.length === 0 ? (
          <p>Нет зарегистрированных рефералов</p>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Телефон</th>
                  <th>Промокод</th>
                  <th>Источник</th>
                  <th>Общий бонус</th>
                  <th>Дата регистрации</th>
                  <th>Статус</th>
                  <th>Дата деактивации</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((referral) => {
                  const isActive =
                    referral.deactivated_at
                      ? new Date(referral.deactivated_at) > new Date()
                      : true;
                  return (
                    <tr key={referral.id}>
                      <td>{referral.id}</td>
                      <td>{referral.referred_email}</td>
                      <td>{referral.referred_phone}</td>
                      <td>
                        {referral.promo_code ? (
                          <code style={{ background: "#f0f0f0", padding: "2px 6px", borderRadius: "4px" }}>
                            {referral.promo_code}
                          </code>
                        ) : (
                          <span style={{ color: "#aaa" }}>—</span>
                        )}
                      </td>
                      <td>{referral.promo_source || <span style={{ color: "#aaa" }}>—</span>}</td>
                      <td>{currencyFormat(referral.total_bonus)}</td>
                      <td>{new Date(referral.created_at).toLocaleDateString()}</td>
                      <td>
                        <span style={{ color: isActive ? "#2e7d32" : "#c62828", fontWeight: 500 }}>
                          {isActive ? "Активен" : "Деактивирован"}
                        </span>
                      </td>
                      <td>
                        {referral.deactivated_at
                          ? new Date(referral.deactivated_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasPromo && (
          <>
            <h4 style={{ marginTop: "24px", marginBottom: "8px" }}>Бонусы по промокодам</h4>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Промокод</th>
                    <th>Источник</th>
                    <th>Процент</th>
                    <th>Сумма бонуса</th>
                    <th>Статус</th>
                    <th>Дата</th>
                  </tr>
                </thead>
                <tbody>
                  {promo_bonuses.map((bonus) => (
                    <tr key={bonus.id}>
                      <td>
                        <code style={{ background: "#f0f0f0", padding: "2px 6px", borderRadius: "4px" }}>
                          {bonus.promo_code}
                        </code>
                      </td>
                      <td>{bonus.promo_source || "—"}</td>
                      <td>{bonus.percent}%</td>
                      <td>{parseFloat(bonus.amount).toFixed(2)}</td>
                      <td>
                        <span style={{ color: bonus.is_paid ? "#2e7d32" : "#f57c00", fontWeight: 500 }}>
                          {bonus.is_paid ? "Выплачен" : "Ожидает выплаты"}
                        </span>
                      </td>
                      <td>{new Date(bonus.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={styles.referralsPage}>
      <div className={styles.card}>
        <h2>Реферальная программа</h2>
        <p>
          Приглашайте новых пользователей и получайте бонусы за каждого
          приглашенного пользователя. Каждый приглашенный пользователь получает
          скидку на первый заказ.
        </p>
      </div>

      <div className={styles.card}>
        <h3>Ваши реферальные ссылки</h3>
        {referralLinks.loading ? (
          <p>Загрузка...</p>
        ) : referralLinks.error ? (
          <p className={styles.error}>Ошибка: {referralLinks.error}</p>
        ) : (
          renderReferralLinks()
        )}
      </div>

      <div className={styles.card}>
        <h3>История рефералов</h3>
        {referralHistory.loading ? (
          <p>Загрузка...</p>
        ) : referralHistory.error ? (
          <p className={styles.error}>Ошибка: {referralHistory.error}</p>
        ) : (
          renderReferralHistory()
        )}
      </div>
    </div>
  );
};

export default ReferralsPage;
